import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertTriangle, Coffee, Moon, Sun, X, Heart, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { createPortal } from 'react-dom';

export default function AsaPopup({ isOpen, onClose, type, userName = "Tim Satala", messageOverride, timeStr }) {
  const [currentDate, setCurrentDate] = useState('');
  const [currentTime, setCurrentTime] = useState(timeStr || '');

  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      setCurrentDate(now.toLocaleDateString('id-ID', options));
      if (!timeStr) {
        setCurrentTime(now.toLocaleTimeString('id-ID', { hour12: false }));
      } else {
        setCurrentTime(timeStr);
      }
    }
  }, [isOpen, timeStr]);

  if (!isOpen) return null;

  // Configurations based on type
  const config = {
    success: {
      color: 'bg-emerald-500',
      lightColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      textColor: 'text-emerald-700',
      icon: <CheckCircle2 className="w-12 h-12 text-emerald-500 mb-2" />,
      title: 'Yeay! Berhasil Absen',
      defaultMessage: `Selamat pagi ${userName}! Semangat bekerja hari ini ya.`,
    },
    late: {
      color: 'bg-rose-500',
      lightColor: 'bg-rose-50',
      borderColor: 'border-rose-200',
      textColor: 'text-rose-700',
      icon: <AlertTriangle className="w-12 h-12 text-rose-500 mb-2" />,
      title: 'Hmm... Agak Telat Ya?',
      defaultMessage: `Halo ${userName}, kamu datang terlambat nih. Lain kali jangan sampai telat lagi ya! Asa perhatiin lho.`,
    },
    overtime: {
      color: 'bg-orange-500',
      lightColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-700',
      icon: <Heart className="w-12 h-12 text-orange-500 mb-2" />,
      title: 'Kerja Lembur Nih?',
      defaultMessage: `Wah ${userName}, pulang jam segini? Jangan lupa jaga kesehatan ya, jangan terlalu memaksakan diri. Hati-hati di jalan pulang!`,
    },
    early_leave: {
      color: 'bg-blue-500',
      lightColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-700',
      icon: <Sun className="w-12 h-12 text-blue-500 mb-2" />,
      title: 'Wih Pulang Cepat!',
      defaultMessage: `Asa juga pengen dong ikut pulang cepat kaya kamu, ${userName}! Hahaha, selamat beristirahat ya!`,
    },
    break_start: {
      color: 'bg-amber-500',
      lightColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      textColor: 'text-amber-700',
      icon: <Coffee className="w-12 h-12 text-amber-500 mb-2" />,
      title: 'Waktunya Istirahat!',
      defaultMessage: `Sudah waktunya istirahat nih ${userName}. Lepaskan pekerjaan sejenak, makan siang yang enak ya!`,
    },
    break_end: {
      color: 'bg-indigo-500',
      lightColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      textColor: 'text-indigo-700',
      icon: <Sun className="w-12 h-12 text-indigo-500 mb-2" />,
      title: 'Waktu Istirahat Selesai',
      defaultMessage: `Selamat datang kembali ${userName}! Mari kita lanjutkan pekerjaan dengan semangat baru!`,
    },
    error: {
      color: 'bg-red-600',
      lightColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-700',
      icon: <XCircle className="w-12 h-12 text-red-500 mb-2" />,
      title: 'Absen Gagal!',
      defaultMessage: `Terjadi kesalahan saat absen. Jika kamu merasa ada di sekitar area pabrik, tolong konfirmasi ke admin ya.`,
    },
    regular_close: {
      color: 'bg-teal-500',
      lightColor: 'bg-teal-50',
      borderColor: 'border-teal-200',
      textColor: 'text-teal-700',
      icon: <Moon className="w-12 h-12 text-teal-500 mb-2" />,
      title: 'Selesai Pekerjaan Hari Ini',
      defaultMessage: `Kerja bagus hari ini ${userName}! Terima kasih atas dedikasinya. Sampai jumpa besok!`,
    }
  };

  const currentConfig = config[type] || config.success;

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        {/* Backdrop (tidak auto-close saat diklik di luar agar user sempat baca & klik oke) */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-xs"
        />

        {/* Modal */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
          className={`relative w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-2xl border-4 ${currentConfig.borderColor}`}
          style={{ zIndex: 10000 }}
        >
          {/* Header Accent */}
          <div className={`h-3 w-full ${currentConfig.color}`}></div>
          
          <button 
            onClick={onClose}
            className="absolute top-5 right-5 p-1 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 z-10"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-8 text-center flex flex-col items-center">
            {/* Animated Icon / Avatar */}
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", bounce: 0.5 }}
              className={`w-24 h-24 rounded-full ${currentConfig.lightColor} border-4 border-white shadow-lg flex items-center justify-center mb-6 relative`}
            >
              {currentConfig.icon}
              {/* Pulsing effect */}
              <div className={`absolute inset-0 rounded-full ${currentConfig.color} opacity-20 animate-ping`}></div>
            </motion.div>

            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {currentConfig.title}
            </h2>
            
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${currentConfig.lightColor} ${currentConfig.textColor} text-xs font-bold mb-4`}>
              <Clock className="w-3.5 h-3.5" />
              {currentTime} • {currentDate}
            </div>

            <p className="text-gray-600 leading-relaxed mb-8">
              {messageOverride || currentConfig.defaultMessage}
            </p>

            <Button 
              onClick={onClose}
              className={`w-full h-12 text-white font-bold rounded-xl shadow-md transition-transform hover:-translate-y-1 ${currentConfig.color} hover:opacity-90`}
            >
              OK, Mengerti
            </Button>
            
            <p className="text-[10px] text-gray-400 mt-4 uppercase tracking-widest font-bold">
              — ASA VIRTUAL ASSISTANT —
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}
